import { logStatsigEvent } from 'atoms/AbTesting/statsigClient';
import { getReferrerHost } from 'src/functions/getReferrerHost';
import { getSessionStorage } from 'src/functions/localStorageHandler';

const PAGE_LANDED_SESSION_KEY = '@statsig:pageLanded';
const FIRST_TOUCH_SESSION_KEY = '@statsig:firstTouch';
const LANDING_SNAPSHOT_SESSION_KEY = '@statsig:landing';

interface PageLandedInput {
  href: string;
  referrer: string;
  firstTouch: boolean;
}

// Statsig types event metadata as string values, so flags are 'true' / 'false'.
const toFlag = (value: boolean): string => String(value);

export const buildPageLandedMetadata = ({ href, referrer, firstTouch }: PageLandedInput): Record<string, string> => {
  const url = new URL(href);
  const params = url.searchParams;
  const hasParam = (name: string): string => toFlag(!!params.get(name));

  return {
    referrer_host: getReferrerHost(referrer),
    utm_source: params.get('utm_source') ?? '',
    utm_medium: params.get('utm_medium') ?? '',
    utm_campaign: params.get('utm_campaign') ?? '',
    has_gclid: hasParam('gclid'),
    has_fbclid: hasParam('fbclid'),
    has_msclkid: hasParam('msclkid'),
    has_li_fat_id: hasParam('li_fat_id'),
    has_gbraid: hasParam('gbraid'),
    has_wbraid: hasParam('wbraid'),
    landing_path: url.pathname,
    first_touch: toFlag(firstTouch),
  };
};

const getSessionStorageSafely = (key: string): string | null => {
  try {
    return getSessionStorage(key);
  } catch {
    return null;
  }
};

const markPageLanded = (): void => {
  try {
    window.sessionStorage.setItem(PAGE_LANDED_SESSION_KEY, 'true');
  } catch {
    // Blocked storage only costs the dedupe; the event is still worth logging.
  }
};

interface LandingSnapshot {
  href: string;
  referrer: string;
}

// Keeps the referrer's scheme and host so getReferrerHost can still parse it.
const reduceReferrerToHost = (referrer: string): string => {
  if (!referrer) return '';
  try {
    const url = new URL(referrer);
    return `${url.protocol}//${url.host}/`;
  } catch {
    return '';
  }
};

// Statsig only exists once the visitor grants consent, which can be several
// pages in. The first page of the tab is saved so page_landed still reports it.
export const snapshotLandingOncePerSession = (): void => {
  if (typeof window === 'undefined') return;

  if (getSessionStorageSafely(LANDING_SNAPSHOT_SESSION_KEY)) return;

  const snapshot: LandingSnapshot = {
    href: window.location.href,
    referrer: reduceReferrerToHost(document.referrer),
  };

  try {
    window.sessionStorage.setItem(LANDING_SNAPSHOT_SESSION_KEY, JSON.stringify(snapshot));
  } catch {
    // Without storage, page_landed falls back to the page it is logged on.
  }
};

const readLandingSnapshot = (): LandingSnapshot | null => {
  const stored = getSessionStorageSafely(LANDING_SNAPSHOT_SESSION_KEY);
  if (!stored) return null;
  try {
    const parsed = JSON.parse(stored);
    return typeof parsed?.href === 'string' && typeof parsed?.referrer === 'string' ? parsed : null;
  } catch {
    return null;
  }
};

export const logPageLandedOncePerSession = (): void => {
  if (typeof window === 'undefined') return;

  if (getSessionStorageSafely(PAGE_LANDED_SESSION_KEY)) return;

  const landing = readLandingSnapshot() ?? { href: window.location.href, referrer: document.referrer };

  const metadata = buildPageLandedMetadata({
    ...landing,
    firstTouch: getSessionStorageSafely(FIRST_TOUCH_SESSION_KEY) === 'true',
  });

  // The guard is set before logging so a second call can never log again.
  markPageLanded();

  logStatsigEvent('page_landed', undefined, metadata);
};
